import { faChevronDown, faChevronRight } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
    Collapse,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material"
import { useState } from "react"
import { makeStyles } from "tss-react/mui"
import useActiveFiltersCountAccounting from "../../../../../shared/components/filters/accountingFiltersDrawer/useActiveFiltersCountAccounting"
import { useInitialDateFilters } from "../../../../../shared/components/table/useInitialDateFilters"
import { TableSummaryWrap, TableTotalAmountBox } from "../../../../../shared/components/table/TableSummaryBox"
import { StandardNoResultText } from "../../../../../shared/components/noResult/StandardNoResultText"
import { StandardFilterButton } from "../../../../../shared/components/table/StandardFilterButton"
import {
    AccountingFilterProps,
    AccountingFiltersDrawer,
    defaultAccountingFilterOptionsList,
} from "../../../../../shared/components/filters/accountingFiltersDrawer/AccountingFiltersDrawer"
import {
    DateType,
    SelectDateTypeFilter,
} from "../../../../../shared/components/filters/selectDateTypeFilter/SelectDateTypeFilter"
import { LoadingSpinner } from "../../../../../shared/components/loading/LoadingSpinner"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { AccountingQuery, fetchBalanceByPeriod } from "../accountingApi"
import { useDateTime } from "../../../../localization/useDateTime"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { toDinero } from "../../../../currency/currencyLib"
import { ExportButtonBalance } from "./ExportButtonBalance"
import { useMoney } from "../../../../currency/useMoney"
import { StandardDateRangeFilter, type DtDateRange } from "../../../../../shared/components/table/StandardDateRangeFilter"

const useStyles = makeStyles()((theme) => ({
    dateFilter: {
        backgroundColor: theme.palette.common.white,
    },
    alignChildren: {
        marginLeft: "2.5rem",
        width: "250px",
    },
    footerCell: {
        padding: 0,
    },
    tableHeaderCell: {
        fontSize: "1.25rem",
        color: "#64748B",
        fontWeight: 500,
    },
    tableLeftHeaderCell: {
        marginLeft: "1rem",
    },
}))

type BalanceTabProps = Readonly<{}>

export function BalanceTab({}: BalanceTabProps) {
    const { classes, cx } = useStyles()
    const dateTimeFactory = useDateTime()
    const moneyFactory = useMoney({})

    const { merchantId } = useMerchant()
    const { startDate, endDate } = useInitialDateFilters()
    const [dateType, setDateType] = useState<DateType>(DateType.ORDER_DATE)
    const [drawerOpen, setDrawerOpen] = useState(false)
    const [open, setOpen] = useState<string[]>([])

    const [filtersList, setFiltersList] = useState<AccountingFilterProps>({
        ...defaultAccountingFilterOptionsList,
        merchantId: merchantId.toString(),
    })

    const [dateState, setDateState] = useState<DtDateRange>({
        startDate,
        endDate,
    })

    const { activeCount } = useActiveFiltersCountAccounting(filtersList)

    const balance = useApiQuery(fetchBalanceByPeriod, {
        queryName: AccountingQuery.BALANCE,
        dependencies: {
            ...filtersList,
            merchantId,
            merchantIds: filtersList.merchantIds?.join(","),
            shopIds: filtersList.shopIds?.join(","),
            paymentMethods: filtersList.paymentMethods?.join(","),
            startDate: dateState.startDate?.toISODate(),
            endDate: dateState.endDate?.toISODate(),
            dateType,
        },
    })

    const handleClick = (clickIndex: string) => {
        if (open.includes(clickIndex)) {
            const openCopy = open.filter((el) => {
                return el !== clickIndex
            })
            setOpen(openCopy)
        } else {
            const openCopy = [...open]
            openCopy.push(clickIndex)
            setOpen(openCopy)
        }
    }

    return (
        <>
            <Grid container wrap="nowrap" alignItems="center" width="100%" paddingBottom="2rem">
                <Grid container gap="1rem" flexWrap="nowrap">
                    <SelectDateTypeFilter
                        dateType={dateType}
                        setDateType={setDateType}
                        className={classes.dateFilter}
                    />
                    <StandardDateRangeFilter
                        value={dateState}
                        onChange={setDateState}
                    />
                </Grid>
                <Grid container width="auto" justifyContent="flex-end" gap="1rem" flexWrap="nowrap">
                    <StandardFilterButton onClick={() => setDrawerOpen(true)} activeCount={activeCount} bordered />

                    <ExportButtonBalance
                        startDate={dateTimeFactory.formatDate(dateState.startDate)}
                        endDate={dateTimeFactory.formatDate(dateState.endDate)}
                        data={balance.data}
                    />
                </Grid>
            </Grid>

            {balance.isLoading && <LoadingSpinner />}
            {!balance.data?.categories && !balance.isFetching && <StandardNoResultText />}

            {balance.data?.categories?.map((category, cIndex) => {
                return (
                    <>
                        <Grid paddingBottom={"1rem"}>
                            {!category.accounts && (
                                <LocalizedStrict id="production-list-no-result">
                                    <Typography variant="body2" sx={{ color: "#64748B", paddingBottom: "0.5rem" }}>
                                        No result
                                    </Typography>
                                </LocalizedStrict>
                            )}
                        </Grid>

                        {category.accounts && (
                            <TableContainer component={Paper} sx={{ marginBottom: "5rem" }}>
                                <Table sx={{ minWidth: 500 }}>
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: "#F8F8F8", textAlign: "center" }}>
                                            <TableCell sx={{ width: "auto" }}>
                                                <Typography
                                                    variant="body2"
                                                    className={cx(classes.tableHeaderCell, classes.tableLeftHeaderCell)}
                                                >
                                                    {category.name}
                                                </Typography>
                                            </TableCell>
                                            <LocalizedStrict id="accounting-amount">
                                                <TableCell
                                                    sx={{ width: "auto", textAlign: "end" }}
                                                    className={classes.tableHeaderCell}
                                                >
                                                    Beløb
                                                </TableCell>
                                            </LocalizedStrict>
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {category?.accounts?.map((account, accIndex) => {
                                            return (
                                                <>
                                                    <TableRow
                                                        key={account.name + accIndex}
                                                        sx={{
                                                            cursor:
                                                                account.paymentMethods &&
                                                                account?.paymentMethods?.length > 0
                                                                    ? "pointer"
                                                                    : "",
                                                        }}
                                                        onClick={() => handleClick(cIndex.toString() + account.name)}
                                                    >
                                                        <TableCell component="th" scope="row" sx={{ width: "250px" }}>
                                                            {account.paymentMethods &&
                                                                account?.paymentMethods?.length > 0 && (
                                                                    <IconButton
                                                                        aria-label="expand row"
                                                                        size="small"
                                                                        style={{ marginRight: "1rem" }}
                                                                    >
                                                                        {open.includes(
                                                                            cIndex.toString() + account.name,
                                                                        ) ? (
                                                                            <FontAwesomeIcon icon={faChevronDown} />
                                                                        ) : (
                                                                            <FontAwesomeIcon icon={faChevronRight} />
                                                                        )}
                                                                    </IconButton>
                                                                )}
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    display: "inline",
                                                                    marginLeft:
                                                                        account.paymentMethods &&
                                                                        account?.paymentMethods?.length > 0
                                                                            ? ""
                                                                            : "2.4rem",
                                                                }}
                                                            >
                                                                {account.name}
                                                            </Typography>
                                                        </TableCell>

                                                        <TableCell
                                                            component="th"
                                                            scope="row"
                                                            sx={{ width: "100px", textAlign: "end" }}
                                                        >
                                                            {moneyFactory.format(toDinero(account.amount))}
                                                        </TableCell>
                                                    </TableRow>

                                                    {open.includes(cIndex.toString() + account.name) &&
                                                        account.paymentMethods && (
                                                            <TableRow
                                                                sx={{ margin: "5rem" }}
                                                                key={account.name + accIndex + 1}
                                                            >
                                                                <TableCell colSpan={2} sx={{ padding: "0" }}>
                                                                    <Collapse
                                                                        in={open.includes(
                                                                            cIndex.toString() + account.name,
                                                                        )}
                                                                        timeout="auto"
                                                                        unmountOnExit
                                                                        sx={{ width: "100%" }}
                                                                    >
                                                                        {account.paymentMethods.map(
                                                                            (paymentMethod, i) => {
                                                                                return (
                                                                                    <Table>
                                                                                        <TableRow
                                                                                            key={account.name + i}
                                                                                            sx={{}}
                                                                                        >
                                                                                            <TableCell
                                                                                                component="th"
                                                                                                scope="row"
                                                                                            >
                                                                                                <Typography
                                                                                                    variant="caption"
                                                                                                    color="text.secondary"
                                                                                                    sx={{
                                                                                                        marginLeft:
                                                                                                            "2.2rem",
                                                                                                    }}
                                                                                                >
                                                                                                    {" "}
                                                                                                    {paymentMethod.name}
                                                                                                </Typography>
                                                                                            </TableCell>

                                                                                            <TableCell
                                                                                                component="th"
                                                                                                scope="row"
                                                                                                sx={{
                                                                                                    textAlign: "end",
                                                                                                }}
                                                                                            >
                                                                                                <Typography
                                                                                                    variant="caption"
                                                                                                    color="text.secondary"
                                                                                                    className={
                                                                                                        classes.alignChildren
                                                                                                    }
                                                                                                >
                                                                                                    {moneyFactory.format(
                                                                                                        toDinero(
                                                                                                            paymentMethod.amount,
                                                                                                        ),
                                                                                                    )}
                                                                                                </Typography>
                                                                                            </TableCell>
                                                                                        </TableRow>
                                                                                    </Table>
                                                                                )
                                                                            },
                                                                        )}
                                                                    </Collapse>
                                                                </TableCell>
                                                            </TableRow>
                                                        )}
                                                </>
                                            )
                                        })}
                                    </TableBody>
                                    <TableFooter>
                                        <TableRow>
                                            <TableCell colSpan={3} className={classes.footerCell}>
                                                <TableSummaryWrap padding="sm">
                                                    <TableTotalAmountBox
                                                        data={category?.accounts}
                                                        accessor={(row) => row.amount}
                                                    />
                                                </TableSummaryWrap>
                                            </TableCell>
                                        </TableRow>
                                    </TableFooter>
                                </Table>
                            </TableContainer>
                        )}
                    </>
                )
            })}
            <AccountingFiltersDrawer
                setFiltersList={setFiltersList}
                drawerOpen={drawerOpen}
                setDrawerOpen={setDrawerOpen}
                filtersList={filtersList}
            />
        </>
    )
}
